import { useCallback } from "react";
import { showToast } from "@/components/ui";
import type { AttributeReviewFieldDecisionInput } from "@/graphql/generated";
import { textOf } from "@/lib/attributeReview/finishedRowChanges";
import {
  undoDecisionFor,
  type fieldDecisionFor,
} from "@/lib/attributeReview/rowDecision";
import { useFinishedEditConfirm } from "./useFinishedEditConfirm";
import type { AttributeReviewMappingRow } from "./useAttributeReview.types";

interface FinishedEditPayload {
  marketplace?: string | null;
  productType?: string | null;
  finishedEdit?: { allowed: boolean; reason?: string | null } | null;
}

export const showEditRefusedToast = () =>
  showToast.error(
    "This session cannot be edited here",
    "Only sessions made by the current sync can be edited one row at a time.",
  );

export function useFinishedEdit(apiPayload: FinishedEditPayload | null) {
  const finishedEdit = apiPayload?.finishedEdit ?? null;
  const { confirmFinishedEdit, finishedEditDialog } = useFinishedEditConfirm({
    editing: Boolean(finishedEdit),
    marketplace: apiPayload?.marketplace ?? null,
    productType: apiPayload?.productType ?? null,
  });

  const blockFinishedSave = useCallback(
    async (
      decisions: AttributeReviewFieldDecisionInput[],
      mappings: AttributeReviewMappingRow[],
      errorTitle: string,
    ): Promise<{ error: string | null } | null> => {
      if (!finishedEdit) return null;
      if (!finishedEdit.allowed) {
        showToast.error(errorTitle, finishedEdit.reason ?? "");
        return { error: finishedEdit.reason ?? null };
      }
      return (await confirmFinishedEdit(decisions, mappings))
        ? null
        : { error: "Cancelled. Nothing was saved." };
    },
    [confirmFinishedEdit, finishedEdit],
  );

  const undoDecision = useCallback(
    (
      mapping: AttributeReviewMappingRow,
      draft: Partial<Parameters<typeof fieldDecisionFor>[1]> | undefined,
    ) =>
      undoDecisionFor(
        String(mapping.id ?? ""),
        { kind: textOf(mapping.kind), leaf: textOf(mapping.leaf), ...draft },
        Boolean(finishedEdit),
      ),
    [finishedEdit],
  );

  return { finishedEdit, finishedEditDialog, blockFinishedSave, undoDecision };
}
